import React, {useState, useEffect, useMemo} from "react"
import {useLocation} from "react-router-dom"
// MUI
import {Box} from "@mui/material"
// Context
import {useData} from "@/context/PackagingDataProvider"
import {useModule} from "@/context/ModuleProvider"
import {useUser} from "@/context/UserProvider"
// Layout (packaging-v3 shell)
import {PackagingLayout, PackagingSection} from "@/components/Layout"
// Views
import {DashboardView, ActivityView} from "@/components/Dashboard"
import {BrandsTab} from "@/components/Brands"
import {ProductsTab} from "@/components/Products"
import {AppsTab} from "@/components/Apps"
import {DownloadTab} from "@/components/Download"
import HealthchecksTab from "@/components/Healthchecks/HealthchecksTab"
import SettingsTab from "@/components/Settings/SettingsTab"
import BatchManagementTab from "@/components/Batches/BatchManagementTab"
import StepSpotlight from "@/components/Layout/StepSpotlight"
import {PackagingStep} from "@/constants/packagingSteps"
import {isMainOrgManager} from "@/utils/orgAccess"
// Onboarding
import IntroductionStepper from "@/components/IntroductionStepper/IntroductionStepper"
// Translation
import {useTranslation} from "react-i18next"
// CSS
import "./Main.css"

const {REACT_APP_DOC_URL} = process.env

// Maps the legacy `?tab=` deep-link values to packaging-v3 sections.
const TAB_TO_SECTION: Record<string, PackagingSection> = {
    products: "products",
    "brands-ranges": "brands",
    batches: "batches",
    download: "download",
    healthchecks: "health",
    apps: "apps",
    dashboard: "dashboard",
    activity: "activity",
    settings: "settings",
}

// - This page checks user data and displays the packaging shell accordingly
// - Redirects to the IntroStepper on first visit / no assets created
// - The sidebar drives which section is rendered in the content area
export default function Main() {

    const {t} = useTranslation(["common"])
    const location = useLocation()
    const {modules} = useModule()
    const {firstVisit, setFirstVisit, commonFilters, setCommonFilters} = useData()
    const {user, fullOrganisation} = useUser()
    // Same gate as the sidebar entry, so a `?tab=healthchecks` deep link
    // cannot open what the rail refuses.
    const canUseHealthchecks = isMainOrgManager(user, fullOrganisation)

    // active sidebar section. The organisation scope is the single, app-wide
    // filter held in PackagingDataProvider (`commonFilters.organisation`) and
    // driven from the sidebar — each tab reads it instead of owning its own.
    const [section, setSection] = useState<PackagingSection>("dashboard")

    // Local-dev preview of the first-visit stepper (triggered from the top bar).
    // Kept as local state — NOT the provider's `firstVisit` — because the
    // provider recomputes firstVisit from asset emptiness on every brands/ranges
    // refetch and would immediately close the dialog on a populated account.
    const [devFirstVisit, setDevFirstVisit] = useState<any>(false)
    const organisationFilter: string = commonFilters?.organisation ?? ""
    const setOrganisationFilter = (value: string) =>
        setCommonFilters({...commonFilters, organisation: value})

    // honour the legacy ?tab= deep links
    useEffect(() => {
        if (location.search.includes("tab")) {
            const tab = new URLSearchParams(location.search).get("tab")
            if (tab && TAB_TO_SECTION[tab]) setSection(TAB_TO_SECTION[tab])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search])

    // A deep link to the healthchecks section is redirected (not just
    // fallback-rendered) once the user and organisation are known and the gate
    // is closed, so the greyed-out rail item is never left as the active one.
    useEffect(() => {
        if (section === "health" && !!user && !!fullOrganisation && !canUseHealthchecks) setSection("dashboard")
    }, [section, user, fullOrganisation, canUseHealthchecks])

    // Resolve the organisation name shown in the dashboard subtitle.
    const orgName = useMemo(() => {
        if (!organisationFilter) return t("all_organisations")
        if (fullOrganisation) {
            if (fullOrganisation.organisation_id === organisationFilter)
                return fullOrganisation.organisation_name
            const sub = fullOrganisation.sub_organisations?.find(
                (s: any) => s.organisation_id === organisationFilter,
            )
            if (sub) return sub.organisation_name
        }
        return t("all_organisations")
    }, [organisationFilter, fullOrganisation, t])

    // Section → translated breadcrumb label.
    const sectionLabels: Record<PackagingSection, string> = {
        dashboard: t("nav_dashboard"),
        settings: t("nav_settings"),
        products: t("products"),
        brands: t("brands_ranges"),
        batches: t("nav_batch_management"),
        download: t("nav_batch_download"),
        health: t("nav_healthcheck"),
        apps: t("apps"),
        activity: t("nav_team_activity"),
    }

    // Getting-started spotlight: a step clicked on the dashboard tracker
    // navigates to its section and highlights the action to perform there.
    const [guideStep, setGuideStep] = useState<PackagingStep | null>(null)
    const handleGuideStep = (step: PackagingStep) => {
        setSection(step.section)
        setGuideStep(step)
    }
    // Manual navigation away from the guided section dismisses the spotlight.
    const handleSectionChange = (s: PackagingSection) => {
        if (guideStep && s !== guideStep.section) setGuideStep(null)
        setSection(s)
    }

    const renderSection = () => {
        switch (section) {
            case "dashboard":
                return <DashboardView orgName={orgName} onNavigate={setSection} onGuideStep={handleGuideStep} />
            case "settings":
                return <SettingsTab />
            case "products":
                return <ProductsTab />
            case "brands":
                return <BrandsTab />
            case "batches":
                return <BatchManagementTab />
            case "download":
                return <DownloadTab />
            case "health":
                return canUseHealthchecks
                    ? <HealthchecksTab />
                    : <DashboardView orgName={orgName} onNavigate={setSection} onGuideStep={handleGuideStep} />
            case "apps":
                return modules.geoloc ? <AppsTab /> : <DashboardView orgName={orgName} onNavigate={setSection} />
            case "activity":
                return <ActivityView />
            default:
                return <DashboardView orgName={orgName} onNavigate={setSection} />
        }
    }

    // The stepper's Dialog renders in a fixed portal and adds no flow height.
    // Without an in-flow placeholder the body collapses to ~0px, which mutes
    // the IntersectionObserver feeding --pkg-visible-* and freezes/poisons
    // dialog positioning (grey-screen soft-lock), and shrinks the iframe
    // height reported to the dashboard. Mirror the Loading screen's height.
    if (firstVisit) return (
        <Box sx={{ minHeight: "80vh" }}>
            <IntroductionStepper {...{firstVisit, setFirstVisit}} />
        </Box>
    )

    // Local-dev preview: same rendering conditions as the real first visit.
    if (devFirstVisit) return (
        <Box sx={{ minHeight: "80vh" }}>
            <IntroductionStepper firstVisit={devFirstVisit} setFirstVisit={setDevFirstVisit} />
        </Box>
    )

    return (
        <PackagingLayout
            section={section}
            onSectionChange={handleSectionChange}
            organisationFilter={organisationFilter}
            setOrganisationFilter={setOrganisationFilter}
            currentCrumb={sectionLabels[section]}
            docLink={`${REACT_APP_DOC_URL}/packaging/introduction`}
            onDevFirstVisit={() => setDevFirstVisit("brands")}
        >
            {renderSection()}
            <StepSpotlight step={guideStep} onClose={() => setGuideStep(null)} />
        </PackagingLayout>
    )
}
